import React, { useState, useEffect, useCallback } from 'react';
import AppShell from '../components/AppShell';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Switch } from '../components/ui/switch';
import {
  Sparkles, Loader2, Pause, Play, Send, Target, ListChecks, Bot, ShieldAlert,
  FlaskConical, Lightbulb, Brain, BarChart3, SlidersHorizontal, ScrollText,
  LayoutDashboard, CheckCircle2, XCircle, AlertTriangle,
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SECTIONS = [
  ['overview', LayoutDashboard, 'Overview'], ['goals', Target, 'Goals'], ['tasks', ListChecks, 'Active Tasks'],
  ['agents', Bot, 'Agents'], ['approvals', ShieldAlert, 'Approvals'], ['experiments', FlaskConical, 'Experiments'],
  ['insights', Lightbulb, 'Insights'], ['memory', Brain, 'Memory'], ['performance', BarChart3, 'Performance'],
  ['settings', SlidersHorizontal, 'Autonomy Settings'], ['audit', ScrollText, 'Audit Log'],
];

const MODE_ORDER = ['assist', 'assist_approval', 'autonomous', 'self_optimizing'];

const Card = ({ title, children, testid }) => (
  <div className="bg-zinc-900/60 border border-zinc-800 rounded-2xl p-4" data-testid={testid}>
    <p className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500 mb-2">{title}</p>
    {children}
  </div>
);

export default function AutonomousDelphiPage() {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };
  const [section, setSection] = useState('overview');
  const [ov, setOv] = useState(null);
  const [sectionData, setSectionData] = useState(null);
  const [advanced, setAdvanced] = useState(false);
  const [chat, setChat] = useState([]);
  const [q, setQ] = useState('');
  const [asking, setAsking] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [busy, setBusy] = useState(null);

  const loadOverview = useCallback(async () => {
    try { setOv((await axios.get(`${API}/autonomy/hub/overview`, { headers })).data); } catch { /* transient */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { loadOverview(); const iv = setInterval(loadOverview, 20000); return () => clearInterval(iv); }, [loadOverview]);

  const SECTION_URLS = {
    goals: '/autonomy/goals', tasks: '/autonomy/tasks', agents: '/autonomy/workforce',
    approvals: '/autonomy/approvals', experiments: '/autonomy/learning/experiments',
    insights: '/autonomy/learning/records?limit=25', memory: '/autonomy/learning/knowledge?limit=40',
    performance: '/autonomy/learning/insights', settings: '/autonomy/meta', audit: '/autonomy/audit?limit=40',
  };

  useEffect(() => {
    if (section === 'overview') return;
    setSectionData(null);
    axios.get(`${API}${SECTION_URLS[section]}`, { headers }).then(r => setSectionData(r.data)).catch(() => setSectionData([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [section, token]);

  const ask = async (question) => {
    const text = (question || q).trim();
    if (!text) return;
    setQ(''); setAsking(true);
    setChat(c => [...c, { role: 'user', text }]);
    try {
      const { data } = await axios.post(`${API}/autonomy/hub/ask`, { question: text, session_id: sessionId }, { headers });
      setSessionId(data.session_id);
      setChat(c => [...c, { role: 'delphi', text: data.answer }]);
      if (data.action_taken === 'paused') loadOverview();
    } catch (e) {
      setChat(c => [...c, { role: 'delphi', text: e.response?.data?.detail || 'Sorry, I could not answer that right now.' }]);
    } finally { setAsking(false); }
  };

  const togglePause = async () => {
    setBusy('pause');
    try {
      await axios.post(`${API}/autonomy/${ov.paused ? 'resume' : 'pause'}`, {}, { headers });
      setOv(o => ({ ...o, paused: !o.paused }));
      toast.success(ov.paused ? 'Delphi resumed' : 'All autonomous activity paused');
    } catch { toast.error('Failed'); } finally { setBusy(null); }
  };

  const setMode = async (mode) => {
    try {
      await axios.put(`${API}/autonomy/hub/mode`, { mode }, { headers });
      setOv(o => ({ ...o, mode }));
      toast.success(`Mode: ${ov.modes[mode].label}`);
    } catch { toast.error('Failed to change mode'); }
  };

  const togglePermission = async (cat, v) => {
    try {
      await axios.put(`${API}/autonomy/settings`, { permissions: { [cat]: v } }, { headers });
      setOv(o => ({ ...o, permissions: { ...o.permissions, [cat]: v } }));
    } catch { toast.error('Failed'); }
  };

  if (!ov) {
    return <AppShell><div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center" data-testid="hub-loading"><Loader2 className="w-8 h-8 text-orange-500 animate-spin" /></div></AppShell>;
  }

  return (
    <AppShell>
      <div className="min-h-screen bg-[#0a0a0f] text-white pb-16" data-testid="autonomous-delphi">
        <div className="border-b border-white/[0.06] bg-[#0d0d14]/80 backdrop-blur-md">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <Sparkles className="w-6 h-6 text-orange-400" />
              <h1 className="text-xl sm:text-2xl font-bold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="hub-title">Autonomous Delphi</h1>
            </div>
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-1.5 text-[11px] text-zinc-500 cursor-pointer">
                <Switch checked={advanced} onCheckedChange={setAdvanced} data-testid="advanced-mode-toggle" />Advanced Mode
              </label>
              <Button onClick={togglePause} disabled={busy === 'pause'} data-testid="hub-pause-btn"
                className={ov.paused ? 'bg-emerald-600 hover:bg-emerald-700 font-bold' : 'bg-red-600 hover:bg-red-700 font-bold'}>
                {ov.paused ? <><Play className="w-4 h-4 mr-2" />RESUME DELPHI</> : <><Pause className="w-4 h-4 mr-2" />PAUSE ALL AUTONOMOUS ACTIVITY</>}
              </Button>
            </div>
          </div>
          {/* Section nav */}
          <div className="max-w-7xl mx-auto px-4 sm:px-6 pb-2 flex gap-1 overflow-x-auto">
            {SECTIONS.map(([id, Icon, label]) => (
              <button key={id} onClick={() => setSection(id)} data-testid={`section-${id}`}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap transition-colors ${section === id ? 'bg-orange-500/15 text-orange-400' : 'text-zinc-500 hover:text-white'}`}>
                <Icon className="w-3.5 h-3.5" />{label}
              </button>
            ))}
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
          {section === 'overview' && (
            <div className="space-y-5">
              {ov.paused && (
                <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-xl flex items-center gap-2 text-sm text-red-300" data-testid="hub-paused-banner">
                  <AlertTriangle className="w-4 h-4" />Delphi is paused. Nothing runs until you resume — your progress is saved.
                </div>
              )}
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-orange-400 mb-3" data-testid="working-on-header">What Delphi is working on</p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Card title="Current Objective" testid="ov-objective">
                    {ov.current_objective ? (
                      <>
                        <p className="text-sm font-semibold text-white">{ov.current_objective.title}</p>
                        <p className="text-xs text-zinc-500 mt-1 line-clamp-2">{ov.current_objective.description}</p>
                      </>
                    ) : <p className="text-xs text-zinc-600">No active objective — create a goal in the Goals section.</p>}
                    <div className="mt-3">
                      <div className="flex justify-between text-[10px] text-zinc-500 mb-1"><span>Progress</span><span>{ov.progress.completed}/{ov.progress.total} tasks</span></div>
                      <div className="h-1.5 bg-zinc-800 rounded-full"><div className="h-1.5 bg-gradient-to-r from-orange-500 to-red-600 rounded-full" style={{ width: `${ov.progress.pct}%` }} /></div>
                    </div>
                  </Card>
                  <Card title="Active Agents" testid="ov-agents">
                    {ov.active_agents.length === 0 && <p className="text-xs text-zinc-600">No agents working right now.</p>}
                    {ov.active_agents.map(a => (
                      <div key={a} className="flex items-center gap-2 py-1 text-xs"><Loader2 className="w-3 h-3 animate-spin text-orange-400" /><span className="text-white">{a.replace(/_/g, ' ')}</span></div>
                    ))}
                    <p className="text-[10px] text-zinc-600 mt-2">{ov.pending_approvals} waiting for your approval</p>
                  </Card>
                  <Card title="Current Tasks" testid="ov-tasks">
                    {ov.current_tasks.length === 0 && <p className="text-xs text-zinc-600">Nothing queued.</p>}
                    {ov.current_tasks.slice(0, 5).map(t => (
                      <div key={t.id} className="flex items-center gap-2 py-1 text-xs">
                        <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${t.status === 'running' ? 'bg-orange-400' : t.status === 'awaiting_approval' ? 'bg-amber-400' : 'bg-sky-400'}`} />
                        <span className="text-zinc-300 truncate">{t.title}</span>
                      </div>
                    ))}
                  </Card>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card title="Recent Decisions" testid="ov-decisions">
                  {ov.recent_decisions.length === 0 && <p className="text-xs text-zinc-600">No decisions yet.</p>}
                  {ov.recent_decisions.slice(0, 4).map((d, i) => (
                    <p key={i} className="text-[11px] text-zinc-400 py-1 border-b border-zinc-800/40 last:border-0"><span className="text-orange-400">{d.agent.replace(/_/g, ' ')}:</span> {d.summary}</p>
                  ))}
                </Card>
                <Card title="Opportunities Discovered" testid="ov-opportunities">
                  {ov.opportunities.length === 0 && <p className="text-xs text-zinc-600">None discovered yet — run a goal or the workforce.</p>}
                  {ov.opportunities.map((o, i) => <p key={i} className="text-[11px] text-zinc-300 py-1">{o.summary}</p>)}
                </Card>
                <Card title="Problems Detected" testid="ov-problems">
                  {ov.problems.length === 0 && (ov.data_gaps || []).length === 0 && <p className="text-xs text-zinc-600">All clear.</p>}
                  {ov.problems.map((p, i) => (
                    <p key={i} className="text-[11px] text-amber-300/90 py-1 flex items-start gap-1"><AlertTriangle className="w-3 h-3 shrink-0 mt-0.5" />{p.message}</p>
                  ))}
                  {(ov.data_gaps || []).map((g, i) => (
                    <p key={`g${i}`} className="text-[11px] text-sky-300/80 py-1 flex items-start gap-1" data-testid="data-gap"><Brain className="w-3 h-3 shrink-0 mt-0.5" />Missing info: {g}</p>
                  ))}
                </Card>
              </div>
              <Card title="Recommended Actions" testid="ov-recommended">
                {ov.recommended_actions.length === 0 && <p className="text-xs text-zinc-600">Nothing recommended right now.</p>}
                {ov.recommended_actions.map(t => (
                  <div key={t.id} className="flex items-center gap-2 py-1.5 text-xs border-b border-zinc-800/40 last:border-0">
                    <Lightbulb className="w-3 h-3 text-orange-400 shrink-0" />
                    <span className="text-zinc-200 flex-1 truncate">{t.title}</span>
                    {t.status === 'awaiting_approval' && <span className="text-[9px] text-amber-400">needs approval</span>}
                  </div>
                ))}
              </Card>

              {/* Ask Delphi */}
              <div className="bg-zinc-900/60 border border-orange-500/20 rounded-2xl p-5" data-testid="ask-delphi">
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-orange-400 mb-3">Ask Delphi</p>
                <div className="space-y-2 max-h-72 overflow-y-auto mb-3" data-testid="ask-chat">
                  {chat.length === 0 && (
                    <div className="flex flex-wrap gap-2">
                      {['What are you working on?', 'What did we learn this week?', 'What should we do next?', 'Show me our biggest growth opportunity.'].map(s => (
                        <button key={s} onClick={() => ask(s)} className="px-3 py-1.5 rounded-full text-[11px] bg-zinc-800 text-zinc-400 hover:text-white hover:bg-zinc-700 transition-colors" data-testid="ask-suggestion">{s}</button>
                      ))}
                    </div>
                  )}
                  {chat.map((m, i) => (
                    <div key={i} className={`text-xs p-3 rounded-xl max-w-[85%] ${m.role === 'user' ? 'bg-orange-500/15 text-orange-100 ml-auto' : 'bg-zinc-800/70 text-zinc-200'}`}>{m.text}</div>
                  ))}
                  {asking && <div className="text-xs text-zinc-500 flex items-center gap-2"><Loader2 className="w-3 h-3 animate-spin" />Delphi is checking its actual activity…</div>}
                </div>
                <div className="flex gap-2">
                  <Input value={q} onChange={e => setQ(e.target.value)} onKeyDown={e => e.key === 'Enter' && ask()}
                    placeholder="Ask Delphi anything about what it's doing…" className="bg-zinc-800/60 border-zinc-700 flex-1" data-testid="ask-input" />
                  <Button onClick={() => ask()} disabled={asking} className="bg-orange-500 hover:bg-orange-600" data-testid="ask-send">
                    {asking ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  </Button>
                </div>
              </div>
            </div>
          )}

          {section === 'settings' && (
            <div className="space-y-5 max-w-3xl" data-testid="hub-settings">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-zinc-500 mb-3">How much should Delphi do on its own?</p>
                <div className="grid grid-cols-1 sm:grid-cols-4 gap-2">
                  {MODE_ORDER.map(mode => (
                    <button key={mode} onClick={() => setMode(mode)} data-testid={`mode-${mode}`}
                      className={`text-left p-3 rounded-xl border transition-all ${ov.mode === mode ? 'border-orange-500/60 bg-orange-500/10' : 'border-zinc-800 bg-zinc-900/40 hover:border-zinc-700'}`}>
                      <p className={`text-xs font-bold uppercase ${ov.mode === mode ? 'text-orange-400' : 'text-white'}`}>{ov.modes[mode].label}</p>
                      <p className="text-[10px] text-zinc-500 mt-1">{ov.modes[mode].desc}</p>
                      {advanced && <p className="text-[9px] text-zinc-600 mt-1">Level {ov.modes[mode].level}</p>}
                    </button>
                  ))}
                </div>
              </div>
              <div data-testid="hub-permissions">
                <p className="text-xs font-semibold uppercase tracking-wider text-zinc-500 mb-2">What Delphi is allowed to do</p>
                {Object.entries(ov.permissions).map(([cat, on]) => (
                  <div key={cat} className="flex items-center justify-between py-2 border-b border-zinc-800/50 last:border-0">
                    <span className="text-sm text-white capitalize">{cat.replace(/_/g, ' ')}</span>
                    <Switch checked={on} onCheckedChange={v => togglePermission(cat, v)} data-testid={`hub-perm-${cat}`} />
                  </div>
                ))}
              </div>
            </div>
          )}

          {section !== 'overview' && section !== 'settings' && (
            <div data-testid={`hub-section-${section}`}>
              {!sectionData ? <div className="py-10 flex justify-center"><Loader2 className="w-6 h-6 text-orange-500 animate-spin" /></div> : (
                <div className="space-y-2">
                  {section === 'agents' && Object.entries(sectionData.agents || {}).map(([id, a]) => (
                    <div key={id} className="p-3 bg-zinc-900/60 border border-zinc-800 rounded-xl">
                      <p className="text-sm font-semibold text-white">{a.label}</p>
                      <p className="text-[11px] text-zinc-500">{advanced ? a.role : a.role.split('.')[0] + '.'}</p>
                    </div>
                  ))}
                  {section === 'performance' && sectionData.knowledge_by_store && (
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                      {[['Things Learned', sectionData.learning_records], ['Experiments', sectionData.experiments.total],
                        ['Concluded', sectionData.experiments.concluded], ['Strategy Versions', sectionData.strategy_versions],
                        ['Verified Facts', sectionData.knowledge_by_epistemic.fact], ['Unproven Claims Blocked', sectionData.downgraded_fact_claims]].map(([l, v]) => (
                        <div key={l} className="bg-zinc-900/60 border border-zinc-800 rounded-xl p-4"><p className="text-xl font-bold text-white">{v}</p><p className="text-[10px] text-zinc-500 uppercase">{l}</p></div>
                      ))}
                    </div>
                  )}
                  {Array.isArray(sectionData) && sectionData.length === 0 && <p className="text-sm text-zinc-600 py-8 text-center">Nothing here yet.</p>}
                  {Array.isArray(sectionData) && sectionData.slice(0, 30).map((item, i) => (
                    <div key={item.id || i} className="p-3 bg-zinc-900/60 border border-zinc-800 rounded-xl text-xs">
                      <div className="flex flex-wrap items-center gap-2">
                        {item.status === 'completed' && <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />}
                        {(item.status === 'failed' || item.status === 'rejected') && <XCircle className="w-3.5 h-3.5 text-red-400" />}
                        <span className="text-white font-medium flex-1 min-w-0 truncate">
                          {item.title || item.hypothesis || item.lesson || item.content || item.action_type || item.reason || ''}
                        </span>
                        {item.status && <span className="text-[9px] px-1.5 py-0.5 rounded bg-zinc-800 text-zinc-400 uppercase">{item.status.replace(/_/g, ' ')}</span>}
                        {item.epistemic && <span className="text-[9px] px-1.5 py-0.5 rounded bg-zinc-800 text-zinc-400 uppercase">{item.epistemic}</span>}
                      </div>
                      {advanced && (item.reason || item.recommendation || item.conclusion) && (
                        <p className="text-[10px] text-zinc-500 mt-1">{item.reason || item.recommendation || item.conclusion}</p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </AppShell>
  );
}
